import React from 'react';
import { Home } from 'lucide-react';
import LoanPageTemplate from '../../components/LoanPageTemplate';

const FirstHomeBuyers = () => (
  <LoanPageTemplate
    guides={[
      { title: 'First Home Buyer Guide Australia 2026', href: '/blog/first-home-buyer-guide', blurb: 'Deposits, grants (including the NT HomeGrown Territory Grant), the 5% Deposit Scheme and each step to settlement.' },
      { title: 'How Much Can I Borrow?', href: '/blog/borrowing-power', blurb: 'How lenders assess income, expenses and the serviceability buffer.' },
      { title: 'Mortgage Broker vs Bank', href: '/blog/broker-vs-bank', blurb: 'What a broker does, how brokers are paid and when going direct makes sense.' },
    ]}
    icon={Home}
    path="/first-home-buyers"
    title="First Home Buyer Loans"
    subtitle="Navigate first home buyer grants, stamp duty relief, and lender requirements across Australia. Our brokers compare loan products to find the right fit for your deposit and income."
    description="Buying your first home is one of the biggest financial decisions you will ever make, and navigating government grants, stamp duty concessions, and lender requirements can feel overwhelming. We simplify the process by comparing loan products to find the right fit for your deposit, income, and lifestyle. Whether you are purchasing in Sydney, Melbourne, Brisbane, or regional Australia, our brokers understand the local market and the incentives available in every state and territory."
    features={[
      {
        title: 'First Home Owner Grant (FHOG)',
        description: 'Grants vary by state and territory, from $10,000 in NSW and Victoria to $30,000 in Queensland and up to $50,000 in the NT (HomeGrown Territory Grant), generally for new homes only. We help you check eligibility and lodge your application alongside your loan.'
      },
      {
        title: 'Stamp Duty Concessions',
        description: 'First home buyers in NSW, VIC, QLD, and other states may pay zero or reduced stamp duty. We calculate your exact savings based on property value and location.'
      },
      {
        title: 'Low Deposit Options from 2%',
        description: 'Some lenders accept deposits as low as 2% for first home buyers. We identify which products suit your savings position and explain the trade-offs involved.'
      },
      {
        title: 'Guarantor Home Loans',
        description: 'Use a family member\'s property equity to avoid Lenders Mortgage Insurance (LMI) and reduce your upfront costs. We structure guarantor loans to protect both parties.'
      },
      {
        title: 'First Home Guarantee Scheme',
        description: 'The Australian Government\'s Home Guarantee Scheme lets eligible buyers purchase with as little as 5% deposit without paying LMI. We manage the application process for you.'
      },
      {
        title: 'Lenders Mortgage Insurance Explained',
        description: 'If your deposit is below 20%, LMI may apply. We compare LMI premiums across lenders and find options that capitalise the cost or waive it entirely for qualifying buyers.'
      }
    ]}
    process={[
      {
        title: 'No-Cost Borrowing Assessment',
        description: 'We review your income, expenses, and savings to determine your maximum borrowing power. You will receive a clear picture of what you can afford before you start looking.'
      },
      {
        title: 'Grant & Scheme Eligibility Check',
        description: 'We identify every government grant, concession, and guarantee scheme you qualify for based on your state, property type, and personal circumstances.'
      },
      {
        title: 'Loan Comparison & Pre-Approval',
        description: 'We compare rates, fees, and features across our panel of 80+ lenders and secure your pre-approval so you can bid with confidence at auction or make an offer.'
      },
      {
        title: 'Settlement & Beyond',
        description: 'We coordinate with your solicitor, lender, and real estate agent through to settlement day. After you move in, we continue monitoring your rate to ensure it stays competitive.'
      }
    ]}
    faqs={[
      {
        q: 'How much deposit do I need for my first home in Australia?',
        a: 'Most lenders prefer a 20% deposit to avoid LMI, but first home buyers can purchase with as little as 2-5% deposit using government schemes like the First Home Guarantee. A 5% deposit on a $600,000 property is $30,000. We help you understand the total upfront costs including stamp duty, legal fees, and inspections.'
      },
      {
        q: 'What is the First Home Owner Grant and am I eligible?',
        a: 'The FHOG is a one-off payment from your state or territory government to help first home buyers. Amounts depend on your state or territory and usually apply to new homes only: for example $10,000 in NSW and Victoria, $30,000 in Queensland, and up to $50,000 under the NT HomeGrown Territory Grant (contracts to 30 September 2027). Caps and conditions change, so check your state revenue office. You must be an Australian citizen or permanent resident, over 18, and never have owned property in Australia before.'
      },
      {
        q: 'Should I use a guarantor to avoid LMI?',
        a: 'A guarantor loan lets a family member (usually a parent) use their property equity as additional security. This can eliminate the need for a 20% deposit and save you thousands in LMI premiums. The guarantor does not make your repayments, their property simply provides extra security until you build enough equity to release them.'
      },
      {
        q: 'Can I buy a first home if I have HECS-HELP debt?',
        a: 'Yes. HECS-HELP debt does affect your borrowing power because lenders include the compulsory repayment in their serviceability calculations. However, it does not prevent you from getting a loan. We factor your HECS balance into the assessment upfront so there are no surprises.'
      },
      {
        q: 'What is the difference between pre-approval and formal approval?',
        a: 'Pre-approval (also called conditional approval) is an in-principle agreement from a lender based on your financial position. It typically lasts 3-6 months. Formal approval happens after the lender values the specific property you want to buy and completes final checks. We manage both stages for you.'
      }
    ]}
    loanType="Home Loan"
    color="from-blue-600 to-blue-800"
  />
);

export default FirstHomeBuyers;
